import { AnnotationModel } from '@/api/storymapCardDetails/queries';
import { Query } from '@/_core/cqrs/types/queries';
import { StorymapRole, WorkspaceRole } from '@/_core/utils/roles';
import { ViewType } from '../../store/types';
import { CardColor } from '@/api/workspaceSettings/queries';
import { BoardVisibility, StorymapCollaboratorModel } from '@/api/common';
import { PublishedStorymapAccessKind } from '../../utils/publishedStorymapRoutes';
import type { PublishedStorymapGateSettings } from '@/pages/main/components/storymapSharing/types';

export * from './integration';

export class StorymapQuery extends Query<StorymapQueryResult> {
    constructor(public StoryMapSlug: string) {
        super('StorymapQuery');
    }
}

export class PublishedStorymapQuery extends Query<PublishedStorymapQueryResult> {
    public AuditOpen = true;

    constructor(public AccessKind: PublishedStorymapAccessKind, public AccessKey: string) {
        super('PublishedStorymapQuery');
    }
}

export class PublishedStorymapPreloadCardDescriptionsQuery extends Query<StorymapPreloadCardDescriptionsQueryResult> {
    constructor(public AccessKind: PublishedStorymapAccessKind, public AccessKey: string) {
        super('PublishedStorymapPreloadCardDescriptionsQuery');
    }
}

export class GetCardDescriptionQuery extends Query<string> {
    constructor(public StorymapId: string, public CardId: string) {
        super('GetCardDescriptionQuery');
    }
}

export class StorymapPreloadCardDescriptionsQuery extends Query<StorymapPreloadCardDescriptionsQueryResult> {
    constructor(public StoryMapSlug: string) {
        super('StorymapPreloadCardDescriptionsQuery');
    }
}

export class AdminSettingsQuery extends Query<AdminSettingsQueryResult> {
    constructor(public StorymapId: string) {
        super('AdminSettingsQuery');
    }
}

export class UserStoryTemplatesQuery extends Query<UserStoryTemplatesResult> {
    constructor(public StorymapId: string) {
        super('UserStoryTemplatesQuery');
    }
}

export class StorymapShareDialogQuery extends Query<StorymapShareDialogQueryResult> {
    constructor(public StorymapId: string) {
        super('StorymapShareDialogQuery');
    }
}

export class StorymapSharingSettingsQuery extends Query<StorymapSharingSettingsResult> {
    constructor(public StorymapId: string) {
        super('StorymapSharingSettingsQuery');
    }
}

export class RefreshLastUpdatedOnCardsQuery extends Query<RefreshLastUpdatedOnCardsResult> {
    constructor(public StorymapId: string) {
        super('RefreshLastUpdatedOnCardsQuery');
    }
}

export class ExpiredToolIntegrationAuthQuery extends Query<ExpiredToolIntegrationAuthResult> {
    constructor(public StorymapId: string) {
        super('ExpiredToolIntegrationAuthQuery');
    }
}

export class ArchivedActivitiesQuery extends Query<ArchivedActivitiesQueryResult> {
    constructor(public StorymapId: string) {
        super('ArchivedActivitiesQuery');
    }
}

export class TrashedCardsQuery extends Query<TrashedCardsResult> {
    constructor(public StorymapId: string) {
        super('TrashedCardsQuery');
    }
}

export class RoadmapDropdownQuery extends Query<RoadmapDropdownQueryResult> {
    constructor(public StorymapId: string) {
        super('RoadmapDropdownQuery');
    }
}

export class SearchStorymapCardsQuery extends Query<string[]> {
    constructor(
        public StorymapId: string,
        public SearchText: string,
        public IsSearchInTitle: boolean,
        public IsSearchInDescription: boolean,
        public IsSearchInComment: boolean,
        public ReleaseId: string
    ) {
        super('SearchStorymapCardsQuery');
    }
}

export interface ArchivedActivitiesQueryResult {
    Activities: ArchivedActivityModel[];
}

export interface ArchivedActivityModel {
    Id: string;
    Title: string;
    ArchivedAt: string;
    ArchivedBy: string;
}

export enum TrashedCardType {
    Unknown = 0,
    Activity = 1,
    Task = 2,
    Subtask = 3,
    UnscheduledSubtask = 4,
}

export enum TrashOperationResultCode {
    Success = 0,
    AlreadyPermanentlyDeleted = 1,
    WrongStorymap = 2,
    AlreadyRestored = 3,
    NoLongerDirectlyTrashed = 4,
    ArchivedActivityRejected = 5,
    ValidationFailed = 6,
}

export interface TrashedCardsResult {
    Rows: TrashedCardRow[];
    CanEmptyTrash: boolean;
    CanEditTrash: boolean;
}

export interface TrashedCardRow {
    Id: string;
    CardType: TrashedCardType;
    Title: string;
    TrashedAt: string;
    TrashedByUserName: string;
    OriginalActivityId?: string;
    OriginalActivityTitle?: string;
    OriginalTaskId?: string;
    OriginalTaskTitle?: string;
    OriginalReleaseId?: string;
    OriginalReleaseName?: string;
    IsUnscheduled: boolean;
    ChildTaskCount: number;
    ChildSubtaskCount: number;
    HasUnavailableActivityWarning: boolean;
    HasUnavailableTaskWarning: boolean;
    HasUnavailableReleaseWarning: boolean;
    HasArchivedReleaseWarning: boolean;
}

export interface ExpiredToolIntegrationAuthResult {
    PromptForExpiredToolIntegrationAuth: boolean;
}

export interface RoadmapDropdownQueryResult {
    Roadmaps: RoadmapDto[];
}

export interface RoadmapDto {
    Id: string;
    Name: string;
}

export interface StorymapQueryResult {
    Name: string;
    Id: string;
    Slug: string;
    ProductSlug: string;
    EstimationUnit: string;
    IsTemplate: boolean;
    Theme: StorymapTheme;
    ActivityColor: CardColor;
    TaskColor: CardColor;
    SubtaskColor: CardColor;
    AboutBoardDescription: string;
    CardKeyPrefix: string;
    CardKeyCounter: number;
    IsCardKeyEnabled: boolean;
    IsSampleStorymap: boolean;
    StorymapAiDatas: StorymapAiDataModel;
    CustomAiStoryTemplates: CustomAiStoryTemplateModel[];
    CustomAiStoryTemplateExamples: CustomAiStoryTemplateModel[];
    StorymapAiTemplateOverrides: StorymapAiTemplateOverrideModel[];
    Releases: ReleaseModel[];
    Activities: ActivityModel[];
    Tasks: TaskModel[];
    Subtasks: SubtaskModel[];
    SubtaskStatuses: SubtaskStatusModel[];

    DiscoveryViewOptions: ViewOptions;
    PlanningViewOptions: ViewOptions;
    BacklogViewOptions: ViewOptions;
    Colors: ColorModel[];
    Personas: PersonaModel[];
    Annotations: AnnotationModel[];
    PrioFrameworkType: StorymapPrioFrameworkType;
    ViewType: ViewType;

    PersonaPictures: PersonaPictureModel[];

    DefaultPersonaCustomFields: PersonaCustomFieldDto[];
    DefaultPersonaPersonalities: PersonaPersonalityDto[];

    CanEdit: boolean;
    CanAdminister: boolean;
    CanCopyPrivateLink: boolean;
    CanCopyPrivateReleaseLink: boolean;
    IsNextGenPrivateLinkSharingEnabled: boolean;
    HasFeedbackPortalAccess: boolean;

    IsIntegrationEnabled: boolean;
    IsImportEnabled: boolean;
    IntegrationToolType: number;
    IsWsLevelIntegrationOauth1: boolean;
    IsDisabledWsLevelIntegration: boolean;
    IsCorporateWsLevelIntegration: boolean;
    IsMissingImpersonationToken: boolean;
    CanPushActivityLevel: boolean;
    CanPushTaskLevel: boolean;
    HasImportableCards: boolean;
    IssueTypeAbbreviations: string;

    ShouldShowOnboarding: boolean;
    IsWorkspaceVisible: boolean;
    ViewersCanCommentOnCards: boolean;
    CanWorkspaceMembersComment: boolean;

    Collaborators: StorymapCollaboratorModel[];
}

export interface PublishedStorymapQueryResult {
    Name: string;
    Id: string;
    Slug: string;
    EstimationUnit: string;
    Theme: StorymapTheme;
    ActivityColor: CardColor;
    TaskColor: CardColor;
    SubtaskColor: CardColor;
    AboutBoardDescription?: string;
    CardKeyPrefix: string;
    CardKeyCounter: number;
    IsCardKeyEnabled: boolean;
    Releases: ReleaseModel[];
    Activities: ActivityModel[];
    Tasks: TaskModel[];
    Subtasks: SubtaskModel[];
    SubtaskStatuses: SubtaskStatusModel[];
    DiscoveryViewOptions: ViewOptions;
    PlanningViewOptions: ViewOptions;
    BacklogViewOptions: ViewOptions;
    Colors: ColorModel[];
    Personas: PersonaModel[];
    Annotations: AnnotationModel[];
    PrioFrameworkType: StorymapPrioFrameworkType;
    ViewType: ViewType;
    CanEdit: boolean;
    CanAdminister: boolean;
    CanComment: boolean;
    Gates: PublishedStorymapGateContext;
}

export interface PublishedStorymapGateContext {
    ShowUserIdentities: boolean;
    ShowComments: boolean;
    ShowWebLinks: boolean;
    ShowEstimation: boolean;
    ShowReleaseDetails: boolean;
    ShowAboutBoard: boolean;
}

export interface StorymapSharingSettingsResult {
    Visibility: BoardVisibility;
    CanWorkspaceMembersComment: boolean;
    IsPublicSharingEnabled: boolean;
    IsNextGenPrivateLinkSharingEnabled: boolean;
    CanCopyNextGenPrivateLink: boolean;
    AllowEditorsToCopyPrivateLinks: boolean;
    Version: number;
    NextGenPrivateLinkCreatedByUser?: string;
    NextGenPrivateLinkCreatedAt?: Date;
    NextGenPublicGates: PublishedStorymapGateSettings;
    NextGenPrivateGates: PublishedStorymapGateSettings;
    PublicAllowAttachments: boolean;
    NextGenPrivateAllowAttachments: boolean;
}

export interface StorymapPreloadCardDescriptionsQueryResult {
    CardDescriptions: { [id: string]: string };
}

export enum StorymapTheme {
    Light = 0,
    Classic = 1,
}

export interface StorymapAiDataModel {
    TargetUsers: string;
    TypeOfProduct: string;
    Purpose: string;
    KeyFeatures: string;
    PlatformCompatibility: string;
    PricingModel: string;
    AdditionalNotes: string;
    Language: string;
}

export interface CustomAiStoryTemplateModel {
    Id: string;
    Name: string;
    Body: string;
    Target: number;
    IsVisibleByDefault: boolean;
}

export interface StorymapAiTemplateOverrideModel {
    LibraryTemplateId: string;
    IsVisible: boolean;
}

export interface ViewOptions {
    IsShowPrioritization: boolean;
    IsShowAnnotation: boolean;
    IsShowPersonaBlock: boolean;
    IsShowIssueType: boolean;
    IsShowAssignee: boolean;
}

export enum StorymapPrioFrameworkType {
    Basic = 0,
    Moscow = 1,
    Kano = 2,
}

export interface PersonaModel {
    Id: string;
    Name: string;
    Description: string;
    Group: string;
    IsGlobal: boolean;

    Picture: string;
    CustomFields: PersonaCustomFieldDto[];
    Personalities: PersonaPersonalityDto[];
}

interface PersonaCustomFieldDto {
    Id: string;
    Label: string;
    Value: string;
}

interface PersonaPersonalityDto {
    Id: string;
    StartingLabel: string;
    EndingLabel: string;
    Value: number;
}

export interface PersonaPictureModel {
    Id: string;
    Path: string;
    IsUploadedByCustomer: boolean;
}

export interface ColorModel {
    DefaultName: string;
    Name: string;
    Color: number;
}

export interface ReleaseModel {
    Id: string;
    Name: string;
    Description: string;
    Order: number;
    IsArchived: boolean;
    ArchivedBy: string;
    ArchivedAt?: string;
    StartDate?: string;
    ReleaseDate?: string;
}

export interface SubtaskStatusModel {
    Key: number;
    Name: string;
    Color: string;
    Category: number;
    Order: number;
}

export interface ActivityModel {
    Id: string;
    Title: string;
    Description: string;
    Order: number;
    Color: number;
    Key: string;
    PersonaIds: string[];
    AnnotationIds: string[];

    HasDescription: boolean;
    HasAttachment: boolean;
    HasComment: boolean;
    HasFeedback: boolean;
    IsWatched?: boolean;

    IsLinkedToIntegrationTool: boolean;
    LinkedUrl: string;
    LinkedItemTypeId: string;
    LinkedItemTypeName: string;

    CreatedAt: Date;
    CreatedByUserId: string;
    LastUpdatedAt: Date;
    LastUpdatedByUserId: string;
    AssigneeDisplayName?: string;
    HasAssignee?: boolean;
    Tasks?: TaskModel[];
}

export interface TaskModel {
    Id: string;
    Title: string;
    Description: string;
    Order: number;
    Color: number;
    Key: string;

    ActivityId: string;
    PersonaIds: string[];
    AnnotationIds: string[];

    HasDescription: boolean;
    HasAttachment: boolean;
    HasComment: boolean;
    HasFeedback: boolean;
    IsWatched?: boolean;

    IsLinkedToIntegrationTool: boolean;
    LinkedUrl: string;
    LinkedItemTypeId: string;
    LinkedItemTypeName: string;

    CreatedAt: Date;
    CreatedByUserId: string;
    LastUpdatedAt: Date;
    LastUpdatedByUserId: string;
    AssigneeDisplayName?: string;
    HasAssignee?: boolean;
    TaskReleases?: TaskReleaseModel[];
    UnsechudedSubtasks?: SubtaskModel[];
}

export interface TaskReleaseModel {
    ReleaseId: string;
    TaskId: string;
    Subtasks: SubtaskModel[];
}

export interface SubtaskModel {
    Id: string;
    Title: string;
    Description: string;
    Order: number;
    ReleaseViewOrder?: number;
    Color: number;
    Estimation?: number;
    Status: number;
    Key: string;

    ReleaseId?: string;
    ActivityId: string;
    TaskId: string;
    PersonaIds: string[];
    AnnotationIds: string[];

    BasicPrioValue: BasicPrioValue;
    MoscowPrioValue: MoscowPrioValue;
    KanoPrioValue: KanoPrioValue;
    BusinessValue: BusinessValue;
    Effort: Effort;

    HasDescription: boolean;
    HasAttachment: boolean;
    HasComment: boolean;
    HasFeedback: boolean;
    IsWatched?: boolean;

    IsLinkedToIntegrationTool: boolean;
    LinkedUrl: string;
    LinkedItemTypeId: string;
    LinkedItemTypeName: string;

    CreatedAt: Date;
    CreatedByUserId: string;
    LastUpdatedAt: Date;
    LastUpdatedByUserId: string;
    AssigneeDisplayName?: string;
    HasAssignee?: boolean;
}

export interface AdminSettingsQueryResult {
    CurrentCollectionId: string;
    Collections: CollectionModel[];
}

export interface UserStoryTemplatesResult {
    IsStoryTemplateEnabled: boolean;
    IsActivityTemplateEnabled: boolean;
    IsTaskTemplateEnabled: boolean;

    StoryTemplate: string;
    ActivityTemplate: string;
    TaskTemplate: string;

    DefaultAiStoryTemplate: string;
    DefaultAiAcceptanceCriteriaTemplate: string;
    AiStoryTemplate: string;
    AiAcceptanceCriteriaTemplate: string;
}

export interface CollectionModel {
    Id: string;
    Name: string;
    Slug: string;
}

export interface ShareDialogWorkspaceMember {
    MemberId: string;
    Name: string;
    Email: string;
    IsInInvitedState: boolean;
    IsMe: boolean;
    WorkspaceRole: WorkspaceRole;
    CollaboratorRole?: StorymapRole;
}

export interface StorymapShareDialogQueryResult {
    AddedWorkspaceMembers: ShareDialogWorkspaceMember[];
    AllWorkspaceMembers: ShareDialogWorkspaceMember[];
}

export interface RefreshLastUpdatedOnCardsResult {
    Activities: RefreshLastUpdatedOnCardsItem[];
    Tasks: RefreshLastUpdatedOnCardsItem[];
    Subtasks: RefreshLastUpdatedOnCardsItem[];
}

export interface RefreshLastUpdatedOnCardsItem {
    Id: string;
    LastUpdatedAt: string;
    LastUpdatedByUserId: string;
}

export enum BasicPrioValue {
    Blocker = 1,
    High = 2,
    Medium = 3,
    Low = 4,
    Trivial = 5,
}

export enum MoscowPrioValue {
    Must = 1,
    Should = 2,
    Could = 3,
    Wont = 4,
}

export enum KanoPrioValue {
    Basic = 1,
    Performance = 2,
    Delighter = 3,
    Indifferent = 4,
    Dissatisfier = 5,
}

export enum BusinessValue {
    High = 5,
    Moderate = 4,
    Low = 3,
    Minor = 2,
    Zero = 1,
}

export enum Effort {
    XL = 1,
    L = 2,
    M = 3,
    S = 4,
    XS = 5,
}

export enum DescriptionFormat {
    Markdown = 0,
    HTML = 1,
}
const DescFormatEnum = DescriptionFormat;

export class CheckImportExcelToStorymapQuery extends Query<CheckImportExcelToStorymapResult> {
    constructor(
        public StorymapId: string,
        public TempFileId: string,
        public DescriptionFormat: DescriptionFormat = DescFormatEnum.Markdown
    ) {
        super('CheckImportExcelToStorymapQuery');
    }
}

export interface CheckImportExcelToStorymapResult {
    Status: ImportStatus;
    Message: string;
    FileName: string;
    Rows: ImportPreviewRow[];
}

export interface ImportPreviewRow {
    RowNumber: number;
    Activity: ImportPreviewCell;
    Task: ImportPreviewCell;
    Subtask: ImportPreviewCell;
    SubtaskDescription: ImportPreviewCell;
    Status: ImportStatus;
}

export interface ImportPreviewCell {
    Value: string;
    Status: ImportStatus;
    Message: string | null;
}

export enum ImportStatus {
    Ok = 'Ok',
    Warning = 'Warning',
    Error = 'Error',
}
